import type { TFunction } from 'i18next';

import type {
  ImageOptimizationStatus,
  ItemStatus,
  LineageItem,
  ProductDisposition,
  Stage,
} from '../types/lineage-images.api.types';
import type { LineagePivotRow } from '../types/lineage-images.types';

import { humanizeCode } from './catalog-labels';
import { imageFailureLabel } from './lineage-image-failures';

/**
 * Q8 — never show a raw `ItemStatus` enum: looks up `labels.itemStatus.<CODE>` in the
 * lineage-images namespace and falls back to a humanised code.
 */
export function itemStatusLabel(
  t: TFunction<'catalog-v2-lineage-images'>,
  status: ItemStatus,
): string {
  return t(`labels.itemStatus.${status}`, { defaultValue: humanizeCode(status) });
}

/** Q8 — human label for a `ProductDisposition` code (IMPORT row outcome), never raw. */
export function dispositionLabel(
  t: TFunction<'catalog-v2-lineage-images'>,
  disposition: ProductDisposition | null | undefined,
): string {
  if (!disposition) return '—';
  return t(`labels.disposition.${disposition}`, { defaultValue: humanizeCode(disposition) });
}

/** Q8 — human label for a source image's optimization status. */
export function imageStatusLabel(
  t: TFunction<'catalog-v2-lineage-images'>,
  status: ImageOptimizationStatus,
): string {
  return t(`labels.imageStatus.${status}`, { defaultValue: humanizeCode(status) });
}

/**
 * A lineage/listing locale scope as people read it: GMC's `en~UK` → "UK · en", Meta's
 * `LANGUAGE_AND_COUNTRY:fr_XX|CA` → "fr_XX|CA", `*` → null (the whole product, no market).
 */
export function formatScope(scope: string | null | undefined): string | null {
  if (!scope || scope === '*') return null;
  const gmc = scope.split('~');
  if (gmc.length === 2 && gmc[0] && gmc[1]) return `${gmc[1]} · ${gmc[0]}`;
  const colon = scope.indexOf(':');
  return colon >= 0 ? scope.slice(colon + 1) : scope;
}

/** The reason fields a lineage row carries (plus its stage, which decides how a code reads). */
interface LineageReasonFields {
  stage?: Stage;
  skipReason: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

/**
 * Finding 1 — CURATION skip reasons that reference another batch. The stable code and the
 * referenced batch's raw id are NOT in the same field for both: `SUPERSEDED_BY_LATER_RUN` files
 * its code on `skipReason` and the superseding run's id on `errorCode`, but `RUN_PRODUCT_HELD`
 * files its code on `errorCode` and the holder's id INSIDE `skipReason`'s free text
 * ("held by run <uuid>") — verified against catalog-service's `catalog_run_item.py`
 * (`file_held` / `file_held_current` write `error_code: "RUN_PRODUCT_HELD"`, `skip_reason:
 * f"held by run {holder_id}"`; `file_superseded_skips` writes `skip_reason: SUPERSEDED_SKIP`,
 * `error_code: <superseding run id>`). `pickReasonCode` / `lineageReasonRunId` below read
 * whichever field actually holds each one — never assume they line up the same way.
 */
export const RUN_REFERENCE_REASONS: ReadonlySet<string> = new Set([
  'RUN_PRODUCT_HELD',
  'SUPERSEDED_BY_LATER_RUN',
]);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Finds a UUID anywhere in free text (`skipReason: "held by run <uuid>"`), not just a full match. */
const UUID_SEARCH_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** True for a bare UUID — never safe to hand to `humanizeCode` or use as an i18n key (Finding 1). */
export function isUuidLike(value: string | null | undefined): boolean {
  return !!value && UUID_RE.test(value);
}

/**
 * The stable code side of a run-reference reason, whichever field the backend actually put it
 * in (see the field-layout note above): `skipReason` for `SUPERSEDED_BY_LATER_RUN`, `errorCode`
 * for `RUN_PRODUCT_HELD`. Falls back to the old `errorCode ?? skipReason` guess for every other
 * reason, where the two fields don't have this swapped layout.
 */
export function pickReasonCode(item: LineageReasonFields): string | null {
  if (item.skipReason && RUN_REFERENCE_REASONS.has(item.skipReason)) return item.skipReason;
  if (item.errorCode && RUN_REFERENCE_REASONS.has(item.errorCode)) return item.errorCode;
  return item.errorCode ?? item.skipReason;
}

/**
 * The batch id a `RUN_PRODUCT_HELD` / `SUPERSEDED_BY_LATER_RUN` row names, when there is one —
 * render it with `RunNameLink` (name + link), never as text. `RUN_PRODUCT_HELD` never puts the
 * id in `errorCode` (that field IS the code); it has to be parsed out of `skipReason`'s prose.
 */
export function lineageReasonRunId(item: LineageReasonFields): string | null {
  if (item.skipReason === 'SUPERSEDED_BY_LATER_RUN') {
    return isUuidLike(item.errorCode) ? item.errorCode : null;
  }
  if (item.errorCode === 'RUN_PRODUCT_HELD') {
    return item.skipReason?.match(UUID_SEARCH_RE)?.[0] ?? null;
  }
  return null;
}

/**
 * A lineage reason code in plain words. IMAGE_GATE's error code is integration's image failure
 * (`HTTPStatusError:404`), labelled by `imageFailureLabel` — never used as an i18n key, whose `:`
 * would read as a namespace separator; other codes go through `labels.reason.<CODE>`.
 *
 * Finding 1 — the stable code for `RUN_PRODUCT_HELD` / `SUPERSEDED_BY_LATER_RUN` comes from
 * `pickReasonCode` (it is NOT always `skipReason` — see the note above), and a bare UUID is
 * never humanised or used as a key — callers that need the referenced batch show it via
 * `lineageReasonRunId` instead.
 */
function reasonCodeLabel(
  t: TFunction<'catalog-v2-lineage-images'>,
  item: LineageReasonFields,
): string | null {
  if (item.stage === 'IMAGE_GATE' && item.errorCode) return imageFailureLabel(t, item.errorCode);
  const code = pickReasonCode(item);
  if (!code || isUuidLike(code)) return null;
  if (code.includes(':')) return humanizeCode(code.replace(/:/g, ' '));
  return t(`labels.reason.${code}`, { defaultValue: humanizeCode(code) });
}

/**
 * Why a lineage item failed or was held back, in plain words: the skip reason / error code
 * through `labels.reason.<CODE>` (humanised fallback), plus the server's message when given.
 */
export function lineageReasonText(
  t: TFunction<'catalog-v2-lineage-images'>,
  item: LineageReasonFields,
): string | null {
  const parts = [reasonCodeLabel(t, item), item.errorMessage].filter((p): p is string => !!p);
  return parts.length > 0 ? parts.join(' — ') : null;
}

/**
 * The one-line reason an issue group leads with: the code's plain label when the row has a code,
 * else the server's message (the newest verbatim one), else "No reason recorded".
 */
export function lineageReasonLabel(
  t: TFunction<'catalog-v2-lineage-images'>,
  item: LineageReasonFields,
): string {
  return reasonCodeLabel(t, item) ?? item.errorMessage ?? t('lineage.issueGroups.noReason');
}

/**
 * The newest-attempt cells of a lineage row that went wrong (failed, or held back for a
 * revisit) — limited to `stages` when given — so the grid and preview name the same problem.
 */
export function issueCells(row: LineagePivotRow, stages?: Stage[]): LineageItem[] {
  const cells = stages ? stages.map((stage) => row.cells[stage]) : Object.values(row.cells);
  return cells.filter(
    (item): item is LineageItem => item?.status === 'FAILED' || item?.status === 'REVISIT',
  );
}

/** Wire connector codes → the storage spelling the images routes check (`GMC` → `GOOGLE_MERCHANT`). */
const STORAGE_CONNECTOR: Record<string, string> = {
  GMC: 'GOOGLE_MERCHANT',
  META: 'META_COMMERCE',
};

/**
 * A run's connector as `images/health` and `images/failures` expect it. Runs may carry either
 * spelling, and those routes validate against storage codes only.
 */
export function toStorageConnector(code: string): string {
  return STORAGE_CONNECTOR[code] ?? code;
}
